import React from 'react';
import type { HelpSectionContent } from '../../content/help/types';
import { trackHelpEvent } from './helpAnalytics';
import type { HelpDownloadConfig, WalkthroughAssetEntry } from '../../content/help/walkthroughTypes';

interface HelpPageShellProps {
  role: 'homeowner' | 'contractor';
  title: string;
  subtitle: string;
  sections: HelpSectionContent[];
  assets?: WalkthroughAssetEntry[];
  downloadConfig?: HelpDownloadConfig;
  pendingSectionStorageKey?: string;
}

interface SearchHit {
  sectionId: string;
  sectionTitle: string;
  match: string;
}

const notificationLegend = [
  { label: 'Action required', color: '#b91c1c', bg: '#fee2e2' },
  { label: 'Informational update', color: '#1d4ed8', bg: '#dbeafe' },
  { label: 'Bid received / selected', color: '#075985', bg: '#e0f2fe' },
  { label: 'Milestone ready / approved', color: '#166534', bg: '#dcfce7' },
  { label: 'Completion', color: '#065f46', bg: '#d1fae5' },
];

export default function HelpPageShell({ role, title, subtitle, sections, assets = [], downloadConfig, pendingSectionStorageKey }: HelpPageShellProps) {
  const [query, setQuery] = React.useState('');
  const [activeSection, setActiveSection] = React.useState(sections[0]?.id || '');
  const [highlightedSection, setHighlightedSection] = React.useState<string>('');
  const [showBackTop, setShowBackTop] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [copiedSectionId, setCopiedSectionId] = React.useState<string>('');
  const refs = React.useRef<Record<string, HTMLElement | null>>({});

  const findHits = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as SearchHit[];
    const hits: SearchHit[] = [];
    sections.forEach((section) => {
      const inSectionTitle = section.title.toLowerCase().includes(q);
      if (inSectionTitle) hits.push({ sectionId: section.id, sectionTitle: section.title, match: `Topic: ${section.title}` });
      section.steps.forEach((step) => {
        const text = `${step.title} ${step.body}`.toLowerCase();
        if (text.includes(q)) hits.push({ sectionId: section.id, sectionTitle: section.title, match: `Step: ${step.title}` });
      });
      (section.faqs || []).forEach((faq) => {
        const text = `${faq.question} ${faq.answer}`.toLowerCase();
        if (text.includes(q)) hits.push({ sectionId: section.id, sectionTitle: section.title, match: `FAQ: ${faq.question}` });
      });
      (section.notifications || []).forEach((n) => {
        const text = `${n.title} ${n.body}`.toLowerCase();
        if (text.includes(q)) hits.push({ sectionId: section.id, sectionTitle: section.title, match: `Notification: ${n.title}` });
      });
    });
    return hits.slice(0, 14);
  }, [query, sections]);

  const jumpToSection = React.useCallback((id: string, source: 'nav' | 'search' | 'anchor' | 'quick') => {
    const el = refs.current[id];
    if (!el) return;
    trackHelpEvent('help_section_clicked', { role, sectionId: id, source });
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
    setHighlightedSection(id);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}#${id}`);
    window.setTimeout(() => setHighlightedSection(''), 1600);
  }, [role]);

  React.useEffect(() => {
    trackHelpEvent('help_page_viewed', { role });
  }, [role]);

  React.useEffect(() => {
    const handler = () => setShowBackTop(window.scrollY > 480);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (top?.target?.id) setActiveSection(top.target.id);
      },
      { rootMargin: '-28% 0px -58% 0px', threshold: [0.2, 0.5] }
    );
    Object.values(refs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections.length]);

  React.useEffect(() => {
    if (pendingSectionStorageKey) {
      const pending = sessionStorage.getItem(pendingSectionStorageKey);
      if (pending && sections.some((s) => s.id === pending)) {
        sessionStorage.removeItem(pendingSectionStorageKey);
        window.setTimeout(() => jumpToSection(pending, 'quick'), 100);
        return;
      }
    }
    const hash = (window.location.hash || '').replace('#', '');
    if (hash && sections.some((s) => s.id === hash)) {
      window.setTimeout(() => jumpToSection(hash, 'anchor'), 120);
    }
  }, [jumpToSection, sections, pendingSectionStorageKey]);

  React.useEffect(() => {
    if (!query.trim()) return;
    trackHelpEvent('help_search_performed', { role, query: query.trim() });
  }, [query, role]);

  const copyLink = async (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      trackHelpEvent('help_section_link_copied', { role, sectionId: id });
      setCopiedSectionId(id);
      window.setTimeout(() => setCopiedSectionId(''), 1200);
    } catch {}
  };

  const assetBySection = React.useMemo(() => {
    const map = new Map<string, WalkthroughAssetEntry>();
    assets.forEach((asset) => map.set(asset.sectionId, asset));
    return map;
  }, [assets]);

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: 24 }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 32, marginBottom: 6 }}>{title}</h1>
          <p style={{ color: '#64748b', margin: 0 }}>{subtitle}</p>
        </div>

        <div style={{ position: 'relative', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 12, minHeight: 52, padding: '0 14px', gap: 10 }}>
            <span aria-hidden>🔎</span>
            <input
              value={query}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => window.setTimeout(() => setSearchOpen(false), 120)}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What do you need help with?"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent' }}
            />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Search topics</span>
          </div>
          {searchOpen && query.trim() && (
            <div style={{ position: 'absolute', top: 56, left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 12px 28px rgba(0,0,0,0.08)', zIndex: 30, maxHeight: 320, overflowY: 'auto' }}>
              {findHits.length === 0 ? (
                <div style={{ padding: 12, color: '#64748b' }}>No results found. Try terms like bid, payment, milestone, or support.</div>
              ) : (
                findHits.map((hit, i) => (
                  <button
                    key={`${hit.sectionId}-${i}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      trackHelpEvent('help_search_result_clicked', { role, sectionId: hit.sectionId, query: query.trim() });
                      jumpToSection(hit.sectionId, 'search');
                    }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 600 }}>{hit.sectionTitle}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{hit.match}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="cb-help-grid" style={{ display: 'grid', gridTemplateColumns: '280px minmax(0,1fr) 280px', gap: 24, alignItems: 'start' }}>
          <aside style={{ position: 'sticky', top: 86, border: '1px solid #e2e8f0', background: '#fff', borderRadius: 12, padding: 12, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Browse topics</div>
            {sections.map((section, idx) => (
              <button
                key={section.id}
                onClick={() => jumpToSection(section.id, 'nav')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 6,
                  borderRadius: 8,
                  border: activeSection === section.id ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                  background: activeSection === section.id ? '#e0f2fe' : '#fff',
                  padding: '10px 12px',
                  color: activeSection === section.id ? '#0c4a6e' : '#334155',
                  cursor: 'pointer',
                }}
              >
                {idx + 1}. {section.title}
              </button>
            ))}
          </aside>

          <main style={{ display: 'grid', gap: 16 }}>
            {sections.map((section) => (
              <article
                key={section.id}
                id={section.id}
                ref={(el) => { refs.current[section.id] = el; }}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 14,
                  background: '#fff',
                  boxShadow: highlightedSection === section.id ? '0 0 0 3px rgba(14,165,233,0.3)' : '0 2px 6px rgba(15,23,42,0.04)',
                  transition: 'box-shadow 220ms ease',
                }}
              >
                <div style={{ padding: 16, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div>
                    <h2 style={{ marginTop: 0, marginBottom: 4 }}>{section.title}</h2>
                    <p style={{ margin: 0, color: '#64748b' }}>{section.description}</p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {section.tags.map((tag) => (
                        <span key={tag} style={{ fontSize: 12, borderRadius: 999, background: '#f1f5f9', color: '#334155', padding: '4px 8px' }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <button className="btn btn-secondary" onClick={() => copyLink(section.id)}>
                    {copiedSectionId === section.id ? 'Copied link' : 'Copy link'}
                  </button>
                </div>

                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px, 360px)', gap: 16 }}>
                  <div style={{ border: '1px solid #d1d5db', borderRadius: 12, overflow: 'hidden', background: '#f8fafc' }}>
                    {(() => {
                      const asset = assetBySection.get(section.id);
                      const preferredImage = asset?.annotatedImage || asset?.rawImage || section.image || asset?.placeholderImage;
                      const alt = asset?.alt || section.imageAlt || section.title;
                      if (!preferredImage) {
                        return (
                          <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                            Screenshot unavailable
                          </div>
                        );
                      }
                      return (
                        <HelpImage
                          image={preferredImage}
                          alt={alt}
                          role={role}
                          sectionId={section.id}
                          captureStatus={asset?.captureStatus}
                        />
                      );
                    })()}
                  </div>
                  <div>
                    <h3 style={{ marginTop: 0 }}>What this screen is</h3>
                    <p style={{ color: '#475569' }}>{section.overview}</p>
                    <h3>What to click / what to do</h3>
                    <ol>
                      {section.steps.map((step) => (
                        <li key={step.title} style={{ marginBottom: 8 }}>
                          <strong>{step.title}:</strong> {step.body}
                        </li>
                      ))}
                    </ol>
                    {section.nextStep && (
                      <div style={{ border: '1px solid #bae6fd', background: '#f0f9ff', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
                        <strong>What happens next:</strong> {section.nextStep}
                      </div>
                    )}
                    {(section.notifications || []).length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <strong>What notifications you may receive</strong>
                        <ul>
                          {(section.notifications || []).map((n) => (
                            <li key={n.title}><strong>{n.title}:</strong> {n.body}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(section.mistakesToAvoid || []).length > 0 && (
                      <div>
                        <strong>Mistakes to avoid</strong>
                        <ul>
                          {(section.mistakesToAvoid || []).map((m) => <li key={m}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {(section.faqs || []).length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: 16 }}>
                    <h3 style={{ marginTop: 0 }}>FAQs</h3>
                    {(section.faqs || []).map((faq, i) => (
                      <details key={faq.question} style={{ marginBottom: 8 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{faq.question}</summary>
                        <div style={{ color: '#475569', marginTop: 6 }}>{faq.answer}</div>
                      </details>
                    ))}
                  </div>
                )}

                <div style={{ borderTop: '1px solid #f1f5f9', padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600 }}>Was this helpful?</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={() => trackHelpEvent('help_feedback_clicked', { role, sectionId: section.id, value: 'yes' })}>Yes</button>
                    <a
                      className="btn btn-primary"
                      href="mailto:support@corebids.com?subject=CoreBids%20Help%20Request"
                      onClick={() => trackHelpEvent('help_feedback_clicked', { role, sectionId: section.id, value: 'still_need_help' })}
                    >
                      Still need help
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </main>

          <aside style={{ position: 'sticky', top: 86, display: 'grid', gap: 12 }}>
            <div style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 12, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Quick Actions</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => jumpToSection(activeSection, 'quick')}>Jump to current topic</button>
                <button className="btn btn-secondary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Back to top</button>
                {downloadConfig?.status === 'live' && (downloadConfig.pptxUrl || downloadConfig.pdfUrl) ? (
                  <a
                    className="btn btn-secondary"
                    href={downloadConfig.pdfUrl || downloadConfig.pptxUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackHelpEvent('help_download_clicked', { role, fileType: downloadConfig.pdfUrl ? 'pdf' : 'pptx' })}
                  >
                    Download walkthrough
                  </a>
                ) : (
                  <button className="btn btn-secondary" disabled title="Walkthrough file link coming soon">
                    Download walkthrough (coming soon)
                  </button>
                )}
                <a
                  className="btn btn-primary"
                  href="mailto:support@corebids.com?subject=CoreBids%20Help%20Support%20Request"
                  onClick={() => trackHelpEvent('help_support_cta_clicked', { role, location: 'quick_actions' })}
                >
                  Contact support
                </a>
              </div>
            </div>

            <div style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 12, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Notifications legend</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {notificationLegend.map((item) => (
                  <div key={item.label} style={{ fontSize: 12, borderRadius: 8, background: item.bg, color: item.color, padding: '6px 8px' }}>{item.label}</div>
                ))}
              </div>
            </div>

            <div style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 12, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Need more help?</div>
              <p style={{ fontSize: 13, color: '#64748b' }}>Include project ID, step, and screenshot so support can resolve faster.</p>
              <a
                className="btn btn-primary"
                href="mailto:support@corebids.com?subject=CoreBids%20Need%20More%20Help"
                onClick={() => trackHelpEvent('help_support_cta_clicked', { role, location: 'utility_rail' })}
              >
                Email support
              </a>
            </div>
          </aside>
        </div>
      </div>

      {showBackTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            borderRadius: 999,
            border: '1px solid #bae6fd',
            background: '#e0f2fe',
            color: '#0c4a6e',
            padding: '10px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            zIndex: 30,
          }}
        >
          Back to top
        </button>
      )}

      <style>{`
        @media (max-width: 1100px) {
          .cb-help-grid {
            grid-template-columns: 1fr !important;
          }
          .cb-help-grid > aside:first-child {
            position: static !important;
            display: flex;
            overflow-x: auto;
            gap: 8px;
            max-height: none !important;
            white-space: nowrap;
          }
          .cb-help-grid > aside:first-child button {
            min-width: max-content;
          }
          .cb-help-grid > aside:last-child {
            position: static !important;
          }
          article > div:nth-child(2) {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function HelpImage({
  image,
  alt,
  role,
  sectionId,
  captureStatus,
}: {
  image: string;
  alt: string;
  role: 'homeowner' | 'contractor';
  sectionId: string;
  captureStatus?: WalkthroughAssetEntry['captureStatus'];
}) {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);

  if (error) {
    return (
      <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
        Screenshot unavailable
      </div>
    );
  }

  return (
    <>
      <button onClick={() => setOpen(true)} style={{ border: 'none', padding: 0, width: '100%', background: 'transparent', cursor: 'zoom-in' }}>
        <img src={image} alt={alt} loading="lazy" onError={() => setError(true)} style={{ width: '100%', display: 'block' }} />
      </button>
      {captureStatus && captureStatus !== 'captured' && (
        <div style={{ padding: '8px 10px', fontSize: 12, color: '#92400e', background: '#fef3c7', borderTop: '1px solid #fde68a' }}>
          Visual status: {captureStatus.replace('-', ' ')}.
        </div>
      )}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <img
            src={image}
            alt={alt}
            style={{ maxWidth: 'min(1200px,95vw)', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}
            onLoad={() => trackHelpEvent('help_section_image_opened', { role, sectionId })}
          />
        </div>
      )}
    </>
  );
}

